import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  LinearProgress,
  Avatar,
} from '@mui/material';
import { Upload, CloudUpload, CheckCircle, Play, Youtube } from 'lucide-mui';
import youtubeImage from './assets/youtube.avif';

interface DemoStep {
  type: 'url' | 'upload' | 'complete';
  progress?: number;
  fileName?: string;
  url?: string;
  showVideo?: boolean;
  thumbnail?: string;
}

const ImportUploadDemo: React.FC = () => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState<DemoStep>({ type: 'url' });
  const [url, setUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [demoPhase, setDemoPhase] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const typingIntervalRef = useRef<number | null>(null);

  const demoUrls = [
    'https://youtube.com/watch?v=dQw4w9WgXcQ',
    'https://vimeo.com/123456789',
  ];

  const demoFiles = [
    'Marketing-Presentation.mp4',
    'Team-Meeting-Recording.mp3',
    'Product-Demo.mov',
  ];

  // Function to stream text character by character
  const streamText = (targetText: string) => {
    // Clear any existing interval
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }

    setUrl(''); // Start with empty string
    let currentIndex = 0;

    typingIntervalRef.current = window.setInterval(() => {
      if (currentIndex < targetText.length) {
        setUrl(targetText.substring(0, currentIndex + 1));
        currentIndex += 1;
        return;
      }

      // Clear interval when done
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    }, 50); // Adjust speed here (50ms = 20 characters per second)
  };

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  // Demo sequence automation
  useEffect(() => {
    let timeoutDuration = 1000;
    if (demoPhase === 0) {
      timeoutDuration = 1000;
    } else if (demoPhase === 2 || demoPhase === 5) {
      timeoutDuration = 100;
    } else {
      timeoutDuration = 2500;
    }

    const timer = setTimeout(() => {
      switch (demoPhase) {
        case 0: // Start with URL demo - stream text instead of setting instantly
          streamText(demoUrls[0]);
          setDemoPhase(1);
          break;
        case 1: // Import URL
          setIsImporting(true);
          setCurrentStep({
            type: 'upload',
            progress: 0,
            fileName: 'Building a Modern React App',
            thumbnail: youtubeImage,
          });
          setDemoPhase(2);
          break;
        case 2: {
          // Progress to 100%
          let progress = 0;
          const progressInterval = setInterval(() => {
            progress += 5;
            const cappedProgress = Math.min(progress, 100);
            setCurrentStep((prev) => ({ ...prev, progress: cappedProgress }));
            if (progress >= 100) {
              clearInterval(progressInterval);
              setDemoPhase(3);
            }
          }, 50);
          break;
        }
        case 3: // Show completion
          setCurrentStep({
            type: 'complete',
            showVideo: true,
            thumbnail: youtubeImage,
            fileName: 'Building a Modern React App',
          });
          setDemoPhase(4);
          break;
        case 4: // Show file upload demo
          // Clear any ongoing streaming
          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
          setUrl('');
          setIsImporting(false);
          setCurrentStep({ type: 'url' });
          setIsDragging(true);
          setDemoPhase(5);
          break;
        case 5: // Simulate file drop
          setIsDragging(false);
          setCurrentStep({
            type: 'upload',
            progress: 0,
            fileName: demoFiles[Math.floor(Math.random() * demoFiles.length)],
          });
          setDemoPhase(6);
          break;
        case 6: {
          // File upload progress
          let fileProgress = 0;
          const fileProgressInterval = setInterval(() => {
            fileProgress += 8;
            const cappedProgress = Math.min(fileProgress, 100);
            setCurrentStep((prev) => ({ ...prev, progress: cappedProgress }));
            if (fileProgress >= 100) {
              clearInterval(fileProgressInterval);
              setDemoPhase(7);
            }
          }, 80);
          break;
        }
        case 7: // File completion
          setCurrentStep({ type: 'complete', showVideo: true });
          setDemoPhase(8);
          break;
        case 8: // Reset
          // Clear any ongoing streaming
          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
          setUrl('');
          setCurrentStep({ type: 'url' });
          setDemoPhase(0);
          break;
        default:
          break;
      }
    }, timeoutDuration);

    return () => clearTimeout(timer);
  }, [demoPhase]);

  const renderHeader = () => (
    <Box sx={{ p: 3, pb: 1.5 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 0.5,
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Upload sx={{ fontSize: 20 }} />
        Add Media
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Import media from YouTube, Vimeo, or upload files from your device.
      </Typography>
    </Box>
  );

  const renderUrlImport = () => (
    <Box sx={{ px: 3, pb: 2 }}>
      <TextField
        label="Media URL"
        variant="outlined"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste a YouTube, Vimeo, or media URL"
        fullWidth
        size="small"
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={() => {}}
        disabled={!url || isImporting}
        fullWidth
        sx={{
          py: 1,
          mb: 1.5,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        }}
      >
        Import URL
      </Button>

      <Box sx={{ textAlign: 'center', my: 1 }}>
        <Typography variant="caption" color="textSecondary">
          or
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          minHeight: '120px',
          border: '2px dashed',
          borderColor: isDragging
            ? theme.palette.primary.main
            : theme.palette.secondary.main,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isDragging
            ? theme.palette.action.hover
            : 'transparent',
          transform: isDragging ? 'scale(1.01)' : 'scale(1)',
          py: 2,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
            '& .MuiSvgIcon-root': {
              transform: 'translateY(-2px)',
            },
          },
        }}
      >
        <Upload
          sx={{
            fontSize: '3rem',
            color: isDragging
              ? theme.palette.primary.main
              : theme.palette.secondary.dark,
            mb: 1.5,
            transition: 'all 0.2s ease-in-out',
            transform: isDragging ? 'translateY(-3px)' : 'none',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: isDragging
              ? theme.palette.primary.main
              : theme.palette.text.primary,
            fontWeight: isDragging ? 500 : 400,
            mb: 1.5,
          }}
        >
          {isDragging ? 'Release to upload' : 'Drag files here'}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          sx={{
            px: 2,
            py: 0.5,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          }}
        >
          Upload files
        </Button>
      </Box>
    </Box>
  );

  const renderUploadProgress = () => (
    <Box sx={{ px: 3, py: 1.5 }}>
      {/* Thumbnail preview */}
      {currentStep.thumbnail && (
        <Box sx={{ mb: 2 }}>
          <Box
            component="img"
            src={currentStep.thumbnail}
            alt="Video thumbnail"
            sx={{
              width: '100%',
              height: 100,
              objectFit: 'cover',
              borderRadius: '6px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Box>
      )}

      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            backgroundColor: theme.palette.secondary.main,
            mt: 0.25,
          }}
        >
          {currentStep.thumbnail ? (
            <Youtube sx={{ fontSize: '1rem', color: 'white' }} />
          ) : (
            <CloudUpload sx={{ fontSize: '1rem' }} />
          )}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.5,
            }}
          >
            {currentStep.fileName}
          </Typography>
          <LinearProgress
            variant={
              currentStep.progress && currentStep.progress >= 99
                ? 'indeterminate'
                : 'determinate'
            }
            value={currentStep.progress || 0}
            sx={{
              height: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.action.hover,
              '& .MuiLinearProgress-bar': {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.secondary.dark,
            fontWeight: 600,
            minWidth: 'fit-content',
            fontSize: '0.75rem',
          }}
        >
          {Math.round(currentStep.progress ?? 0)}%
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}
      >
        Processing media...
      </Typography>
    </Box>
  );

  const renderComplete = () => (
    <Box sx={{ px: 3, py: 1.5, textAlign: 'center' }}>
      {/* Video preview with thumbnail */}
      {currentStep.showVideo && (
        <Box sx={{ mb: 2, position: 'relative' }}>
          <Box
            component="img"
            src={currentStep.thumbnail || youtubeImage}
            alt="Video preview"
            sx={{
              width: '100%',
              height: 120,
              objectFit: 'cover',
              borderRadius: '6px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Play
              sx={{
                fontSize: '1.2rem',
                color: 'white',
                ml: 0.25,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.9 },
                  '50%': { opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Success state */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <CheckCircle
          sx={{
            fontSize: '1.2rem',
            color: theme.palette.success.main,
            animation: 'checkmark 0.5s ease-in-out',
            '@keyframes checkmark': {
              '0%': { transform: 'scale(0)', opacity: 0 },
              '50%': { transform: 'scale(1.2)', opacity: 1 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: theme.palette.success.dark,
          }}
        >
          Import Complete!
        </Typography>
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: '0.75rem' }}
      >
        {currentStep.fileName || 'Media'} has been processed and is ready to use
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {renderHeader()}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.action.hover,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: 3,
          },
        }}
      >
        {currentStep.type === 'url' && renderUrlImport()}
        {currentStep.type === 'upload' && renderUploadProgress()}
        {currentStep.type === 'complete' && renderComplete()}
      </Box>
    </Box>
  );
};

export default ImportUploadDemo;
